"use client";

import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormInput,
  FormNumber,
  FormRoot,
  Label,
  Loader,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useParams, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { shopRolesApi } from "../api/client";
import { getPermissionGroups } from "../lib/permission-labels";
import { RequireMerchantPermission } from "../ui/RequireMerchantPermission";
import { ShopRoleFormToolbar } from "../ui/ShopRoleFormToolbar";

const ShopRoleFormSchema = z.object({
  name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ບົດບາດ"),
  permissions: z.array(z.string()),
  sortOrder: z.number().int().optional(),
});

type FormValues = z.infer<typeof ShopRoleFormSchema>;

export function ShopRoleFormPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const roleId = params?.roleId as string | undefined;
  const isEdit = roleId != null && roleId !== "new";
  const router = useRouter();
  const [availablePerms, setAvailablePerms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const form = RHF.useForm<FormValues>({
    resolver: zodResolver(ShopRoleFormSchema),
    defaultValues: {
      name: "",
      permissions: [],
      sortOrder: 0,
    },
  });

  const load = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const permsRes = await shopRolesApi.getAvailablePermissions(shopId);
      setAvailablePerms(permsRes.items);
      if (isEdit && roleId) {
        const role = await shopRolesApi.get(shopId, roleId);
        form.reset({
          name: role.name,
          permissions: role.permissions,
          sortOrder: role.sortOrder,
        });
      }
    } catch {
      setAvailablePerms([]);
    } finally {
      setLoading(false);
    }
    // form from useForm is stable; omit to avoid effect re-running every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, isEdit, roleId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (values: FormValues) => {
    if (!shopId) return;
    setSubmitting(true);
    try {
      if (isEdit && roleId) {
        await shopRolesApi.update(shopId, roleId, {
          name: values.name,
          permissions: values.permissions,
          sortOrder: values.sortOrder,
        });
        toast.success("ແກ້ໄຂບົດບາດສຳເລັດ");
      } else {
        await shopRolesApi.create(shopId, {
          name: values.name,
          permissions: values.permissions,
          sortOrder: values.sortOrder ?? 0,
        });
        toast.success("ສ້າງບົດບາດສຳເລັດ");
      }
      window.history.back();
    } catch {
      // toast from fetcher
    } finally {
      setSubmitting(false);
    }
  };

  if (!shopId) {
    return (
      <div className="container py-8">
        <p className="text-muted-foreground">ບໍ່ພົບລາຍການ.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  const groups = getPermissionGroups(availablePerms);
  const selected = form.watch("permissions") as string[];

  const toggleGroup = (groupPermIds: string[], checked: boolean) => {
    const current = new Set(selected);
    groupPermIds.forEach((id) => {
      if (checked) current.add(id);
      else current.delete(id);
    });
    form.setValue("permissions", Array.from(current));
  };

  return (
    <RequireMerchantPermission
      permission={
        isEdit
          ? MerchantPermissions.shop_roles.update
          : MerchantPermissions.shop_roles.create
      }
    >
      <Header>
        <Breadcrumb items={shopBreadcrumbs.roleForm(shopId, isEdit)} />
      </Header>

      <Main>
        <ShopRoleFormToolbar
          isEdit={isEdit}
          onBack={() => router.history.back()}
        />
        <Card>
          <CardContent className="pt-6">
            <FormRoot<FormValues>
              methods={form}
              onSubmit={(vals) => onSubmit(vals)}
              className="space-y-6"
            >
              <div className="space-y-6">
                <FormInput
                  name="name"
                  label="ຊື່ບົດບາດ"
                  placeholder="ຕົວຢ່າງ: ຜູ້ຂາຍ"
                  requiredMark
                />

                <FormNumber name="sortOrder" label="ລຳດັບ (ຕົວເລກນ້ອຍ = ແຖວເທິງ)" />

                <div>
                  <p className="mb-3 font-medium text-muted-foreground text-sm">
                    ສິດການເຂົ້າເຖິງ
                  </p>
                  <div className="space-y-3">
                    {groups.map((g) => {
                      const ids = g.items.map((i) => i.id);
                      const allChecked =
                        ids.length > 0 &&
                        ids.every((id) => selected.includes(id));
                      const selectedInGroup = ids.filter((id) =>
                        selected.includes(id),
                      ).length;
                      return (
                        <Card
                          key={g.groupLabel}
                          className="gap-0 overflow-hidden border bg-card py-0 shadow-sm transition-colors hover:border-primary/20"
                        >
                          <div className="flex items-center gap-3 border-b bg-muted/30 px-4 py-2.5">
                            <Checkbox
                              id={`perm-group-${g.groupLabel}`}
                              checked={allChecked}
                              onCheckedChange={(checked) =>
                                toggleGroup(ids, checked === true)
                              }
                              className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                            />
                            <Label
                              htmlFor={`perm-group-${g.groupLabel}`}
                              className="flex-1 cursor-pointer font-semibold text-sm"
                            >
                              {g.groupLabel}
                            </Label>
                            <Badge
                              variant="secondary"
                              className="shrink-0 font-mono text-xs tabular-nums"
                            >
                              {selectedInGroup}/{ids.length}
                            </Badge>
                          </div>
                          <div className="grid gap-0.5 p-2 sm:grid-cols-2">
                            {g.items.map((item) => (
                              <Label
                                htmlFor={`perm-${item.id}`}
                                key={item.id}
                                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent/50"
                              >
                                <Checkbox
                                  id={`perm-${item.id}`}
                                  checked={selected.includes(item.id)}
                                  onCheckedChange={(checked) => {
                                    const next =
                                      checked === true
                                        ? [...selected, item.id]
                                        : selected.filter((x) => x !== item.id);
                                    form.setValue("permissions", next, {
                                      shouldDirty: true,
                                    });
                                  }}
                                  className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                                />
                                <span className="text-foreground/90">
                                  {item.label}
                                </span>
                              </Label>
                            ))}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" disabled={submitting}>
                    {submitting ? <Loader /> : null}
                    <span className="ml-2">{isEdit ? "ບັນທຶກ" : "ສ້າງບົດບາດ"}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => router.history.back()}
                  >
                    ຍົກເລີກ
                  </Button>
                </div>
              </div>
            </FormRoot>
          </CardContent>
        </Card>
      </Main>
    </RequireMerchantPermission>
  );
}
